import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', loginData);
      const user = response.data.user;
      
      // Check if user is admin or staff
      if (user.role !== 'admin' && user.role !== 'staff' && user.role !== 'volunteer') {
        toast.error('Access denied. This login is for staff members only.');
        setLoading(false);
        return;
      }
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 relative overflow-hidden hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1576108700272-8d9f86c51273?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxibGFjayUyMHN0dWRlbnRzJTIwcmVhZGluZyUyMGJvb2tzJTIwbGlicmFyeSUyMGdyb3VwJTIwc21pbGV8ZW58MHx8fHwxNzcxNDMwNTY4fDA&ixlib=rb-4.1.0&q=85"
          alt="Happy child reading book"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70"></div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-stone-200 shadow-floating" data-testid="admin-login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 shadow-sm">
              <Shield className="h-12 w-12 mx-auto text-primary" />
            </div>
            <CardTitle className="text-3xl">Admin Portal</CardTitle>
            <CardDescription>Sign in to access the administration dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-username">Username</Label>
                <Input
                  id="admin-username"
                  data-testid="admin-username-input"
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <PasswordInput
                  id="admin-password"
                  data-testid="admin-password-input"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                data-testid="admin-login-submit-button"
                className="w-full rounded-full"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <div className="text-center pt-2">
                <Link 
                  to="/login"
                  className="text-sm text-stone-600 hover:text-primary hover:underline"
                  data-testid="family-signin-link"
                >
                  ← Back to Family Sign In
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
